<section class="admin-section" data-test="settings-editor">
  <h2 class="admin-h2">Settings</h2>
  <p class="admin-lede">Values that change how Tuition Management behaves for everyone. A saved change applies within about 30 seconds.</p>

  @if (error) {
    <div class="tw:my-2 tw:p-3 tw:border tw:border-red-500 tw:bg-red-50 tw:text-red-900 tw:rounded" data-test="settings-error">{{error}}</div>
  }

  @if (loading) {
    <p>Loading…</p>
  }

  @if (!loading && settings.length > 0) {
    @for (group of groups; track group.category) {
      <div class="tw:mt-6" data-test="settings-group">
        <h3 class="admin-h3" data-test="settings-group-title">{{group.category}}</h3>
        <div class="tw:overflow-x-auto">
          <table class="admin-table">
            <thead>
              <tr>
                <th class="tw:text-left">Setting</th>
                <th class="tw:text-left tw:w-64">Value</th>
                <th class="tw:text-left tw:w-56">Last changed</th>
                <th class="tw:text-left tw:w-40"></th>
              </tr>
            </thead>
            <tbody>
              @for (setting of group.settings; track setting.settingKey) {
                <tr data-test="setting-row">
                  <td>
                    <strong>{{setting.displayName}}</strong>
                    @if (isPerBrand(setting)) {
                      <span class="tw:ml-2 tw:inline-block tw:px-2 tw:py-0.5 tw:text-xs tw:font-semibold tw:rounded tw:bg-amber-100 tw:text-amber-900 tw:border tw:border-amber-400"
                        data-test="setting-per-brand">Per brand — review for collapse</span>
                    }
                    @if (setting.description) {
                      <div class="tw:text-sm tw:opacity-75">{{setting.description}}</div>
                    }
                    <div class="tw:text-xs tw:opacity-50">{{setting.settingKey}}</div>
                  </td>
                  <td data-label="Value">
                    @if (isPerBrand(setting)) {
                      <div class="tw:text-xs tw:opacity-60">All other brands</div>
                    }
                    @if (isBool(setting)) {
                      <label class="field__toggle">
                        <input type="checkbox" [checked]="current(setting) === 'true'" [disabled]="saving === setting.settingKey"
                          (change)="edit(setting, $any($event.target).checked ? 'true' : 'false')" data-test="setting-bool" />
                        <span class="field__toggle-track"></span>
                      </label>
                    } @else if (isInt(setting)) {
                      <input type="number" class="field__control" step="1"
                        [attr.min]="setting.minValue" [attr.max]="setting.maxValue"
                        [value]="current(setting)" [disabled]="saving === setting.settingKey"
                        (input)="edit(setting, $any($event.target).value)" data-test="setting-int" />
                      @if (setting.minValue != null || setting.maxValue != null) {
                        <div class="tw:text-xs tw:opacity-60">
                          @if (setting.minValue != null && setting.maxValue != null) {
                            {{setting.minValue}} to {{setting.maxValue}}
                          } @else if (setting.minValue != null) {
                            At least {{setting.minValue}}
                          } @else {
                            At most {{setting.maxValue}}
                          }
                        </div>
                      }
                    } @else {
                      <input type="text" class="field__control" [value]="current(setting)" [disabled]="saving === setting.settingKey"
                        (input)="edit(setting, $any($event.target).value)" data-test="setting-string" />
                    }
                    @if (isDirty(setting) && validate(setting)) {
                      <div class="tw:text-sm tw:text-red-700" data-test="setting-invalid">{{validate(setting)}}</div>
                    }
                  </td>
                  <td data-label="Last changed">
                    @if (setting.updatedUtc) {
                      <admin-email [value]="setting.updatedBy || 'unknown'" /><br />
                      <span class="tw:text-sm tw:opacity-75">{{setting.updatedUtc | date:'dd/MM/yyyy HH:mm'}}</span>
                    } @else {
                      <span class="tw:text-sm tw:opacity-50">Never changed</span>
                    }
                  </td>
                  <td>
                    <button type="button" class="btn" [disabled]="!canSave(setting)" (click)="save(setting)" data-test="setting-save">Save</button>
                    <button type="button" class="btn" (click)="history(setting)" data-test="setting-history">History</button>
                    @if (isPerBrand(setting)) {
                      <button type="button" class="btn" [disabled]="saving === setting.settingKey" (click)="collapse(setting)" data-test="setting-collapse">Collapse to one value</button>
                    }
                  </td>
                </tr>
                @if (isPerBrand(setting)) {
                  @for (brand of brands; track brand.companyId) {
                    <tr data-test="setting-brand-row">
                      <td class="tw:pl-6 tw:text-sm">
                        {{brand.name}}
                        @if (brandValue(setting, brand.companyId) === null) {
                          <span class="tw:opacity-60"> — uses the value above</span>
                        }
                      </td>
                      <td>
                        @if (isBool(setting)) {
                          <label class="field__toggle">
                            <input type="checkbox" [checked]="currentBrand(setting, brand.companyId) === 'true'" [disabled]="saving === brandKey(setting, brand.companyId)"
                              (change)="editBrand(setting, brand.companyId, $any($event.target).checked ? 'true' : 'false')" data-test="setting-brand-bool" />
                            <span class="field__toggle-track"></span>
                          </label>
                        } @else {
                          <input [type]="isInt(setting) ? 'number' : 'text'" class="field__control"
                            [attr.min]="isInt(setting) ? setting.minValue : null" [attr.max]="isInt(setting) ? setting.maxValue : null"
                            [value]="currentBrand(setting, brand.companyId)" [disabled]="saving === brandKey(setting, brand.companyId)"
                            (input)="editBrand(setting, brand.companyId, $any($event.target).value)" data-test="setting-brand-input" />
                        }
                        @if (isBrandDirty(setting, brand.companyId) && validateBrand(setting, brand.companyId)) {
                          <div class="tw:text-sm tw:text-red-700" data-test="setting-brand-invalid">{{validateBrand(setting, brand.companyId)}}</div>
                        }
                      </td>
                      <td></td>
                      <td>
                        <button type="button" class="btn" [disabled]="!canSaveBrand(setting, brand.companyId)"
                          (click)="saveBrand(setting, brand.companyId)" data-test="setting-brand-save">Save {{brand.name}}</button>
                      </td>
                    </tr>
                  }
                }
              }
            </tbody>
          </table>
        </div>
      </div>
    }
  }

  @if (!loading && !error && settings.length === 0) {
    <p class="tw:opacity-60" data-test="settings-empty">No settings are configured.</p>
  }
</section>
